import React, { useState, useCallback, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { jsxs, jsx } from 'react/jsx-runtime';
import { createPortal } from 'react-dom';

// src/components/Button.tsx
function cn(...inputs) {
  return clsx(inputs);
}
var Button = React.forwardRef(
  ({ variant = "primary", size = "md", loading, icon, iconPosition = "left", className, children, disabled, ...props }, ref) => {
    const baseStyles = "inline-flex items-center justify-center gap-2 whitespace-nowrap select-none font-medium border-[1.5px] transition-[transform,background-color,border-color,box-shadow,color] duration-200 ease-[cubic-bezier(0.2,0,0,1)] disabled:opacity-40 disabled:cursor-not-allowed";
    const variantStyles2 = {
      // `bg-primary` on a <button> is what the glass layer paints with the
      // brand gradient + glow (packages/ui/src/glass-theme.css), so the button
      // matches every other primary CTA in the suite. The ink is the brand's
      // derived foreground, which the branding layer computes from luminance.
      primary: "bg-primary text-primary-foreground border-transparent",
      // Bordered neutral. `border-border` + `bg-card` is the pairing the glass
      // layer turns into its secondary button (white glass, e1, strong border
      // on hover).
      secondary: "bg-card border-border text-foreground",
      ghost: "bg-card border-border text-foreground",
      dark: "bg-foreground text-background border-transparent hover:opacity-90",
      danger: "bg-red-600 text-white border-transparent",
      success: "bg-[var(--success)] text-white border-transparent hover:brightness-110"
    };
    const sizeStyles3 = {
      sm: "h-9 px-3.5 text-[13px] rounded-[var(--radius-tap,16px)]",
      md: "h-10 px-[18px] text-[13px] rounded-[var(--radius-tap,16px)]",
      lg: "h-[3rem] px-6 text-[15px] rounded-[var(--radius-tap-lg,24px)]"
    };
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        disabled: disabled || loading,
        className: cn(baseStyles, variantStyles2[variant], sizeStyles3[size], className),
        ...props,
        children: [
          loading && /* @__PURE__ */ jsxs("svg", { className: "animate-spin h-4 w-4", viewBox: "0 0 24 24", children: [
            /* @__PURE__ */ jsx(
              "circle",
              {
                className: "opacity-25",
                cx: "12",
                cy: "12",
                r: "10",
                stroke: "currentColor",
                strokeWidth: "4",
                fill: "none"
              }
            ),
            /* @__PURE__ */ jsx(
              "path",
              {
                className: "opacity-75",
                fill: "currentColor",
                d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              }
            )
          ] }),
          icon && iconPosition === "left" && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: icon }),
          children,
          icon && iconPosition === "right" && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: icon })
        ]
      }
    );
  }
);
Button.displayName = "Button";
var Input = React.forwardRef(
  ({ label, error, helperText, endAdornment, className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsx(
        "label",
        {
          className: "block text-sm font-medium mb-1.5",
          style: { color: "var(--field-label, var(--text-secondary))" },
          children: label
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            className: cn(
              "w-full rounded-lg text-sm transition-all duration-150 outline-none",
              "bg-[var(--bg-surface-2)] border border-[var(--border)] text-[var(--text-primary)]",
              "px-3 py-2",
              endAdornment && "pe-9",
              "placeholder:text-[var(--text-muted)]",
              "focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-dim)]",
              error && "border-[var(--danger)] focus:border-[var(--danger)] focus:ring-[var(--danger)]",
              className
            ),
            "aria-invalid": error ? true : void 0,
            ...props
          }
        ),
        endAdornment && /* @__PURE__ */ jsx("div", { className: "absolute inset-y-0 end-0 flex items-center", children: endAdornment })
      ] }),
      error && /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs", style: { color: "var(--field-error-text, var(--danger))" }, children: error }),
      helperText && !error && /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs", style: { color: "var(--field-help, var(--text-muted))" }, children: helperText })
    ] });
  }
);
Input.displayName = "Input";
var EyeIcon = () => /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [
  /* @__PURE__ */ jsx("path", { d: "M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" }),
  /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "3" })
] });
var EyeOffIcon = () => /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [
  /* @__PURE__ */ jsx("path", { d: "M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" }),
  /* @__PURE__ */ jsx("path", { d: "M14.084 14.158a3 3 0 0 1-4.242-4.242" }),
  /* @__PURE__ */ jsx("path", { d: "M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" }),
  /* @__PURE__ */ jsx("path", { d: "m2 2 20 20" })
] });
var PasswordInput = React.forwardRef(
  ({ className, ...props }, ref) => {
    const [visible, setVisible] = useState(false);
    return /* @__PURE__ */ jsx(
      Input,
      {
        ref,
        type: visible ? "text" : "password",
        className: cn("weco-pwd-input", className),
        endAdornment: /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: () => setVisible((v) => !v),
            className: "flex h-full items-center px-2.5 transition-colors",
            style: { color: "var(--text-muted)" },
            onMouseEnter: (e) => {
              e.currentTarget.style.color = "var(--text-primary)";
            },
            onMouseLeave: (e) => {
              e.currentTarget.style.color = "var(--text-muted)";
            },
            tabIndex: -1,
            "aria-label": visible ? "Hide password" : "Show password",
            children: visible ? /* @__PURE__ */ jsx(EyeOffIcon, {}) : /* @__PURE__ */ jsx(EyeIcon, {})
          }
        ),
        ...props
      }
    );
  }
);
PasswordInput.displayName = "PasswordInput";
var Card = React.forwardRef(
  ({ hover = false, padding = "md", className, children, ...props }, ref) => {
    const paddingStyles = {
      none: "",
      sm: "p-3",
      md: "p-4",
      lg: "p-6"
    };
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg transition-all",
          hover && "hover:border-[var(--accent-border)] hover:-translate-y-1 hover:shadow-[var(--shadow-card)]",
          paddingStyles[padding],
          className
        ),
        ...props,
        children
      }
    );
  }
);
Card.displayName = "Card";
var EmptyState = ({
  title,
  description,
  icon,
  action,
  className
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("flex items-center justify-center min-h-[400px] p-8", className),
      style: { background: "var(--bg-root)" },
      children: /* @__PURE__ */ jsxs("div", { className: "text-center max-w-md animate-fade-in", children: [
        icon && /* @__PURE__ */ jsx("div", { className: "mb-6 flex justify-center opacity-20", children: icon }),
        /* @__PURE__ */ jsx(
          "h3",
          {
            className: "text-lg font-bold mb-2",
            style: { color: "var(--text-primary)" },
            children: title
          }
        ),
        description && /* @__PURE__ */ jsx(
          "p",
          {
            className: "text-sm mb-6",
            style: { color: "var(--text-muted)" },
            children: description
          }
        ),
        action && /* @__PURE__ */ jsx("div", { className: "flex justify-center", children: action })
      ] })
    }
  );
};
var PageHeader = ({
  title,
  subtitle,
  actions,
  className
}) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "shrink-0 flex items-center justify-between flex-wrap gap-4 py-4 px-4 sm:py-6 sm:px-8 border-b",
        className
      ),
      style: {
        borderColor: "var(--border-primary)",
        background: "var(--bg-primary)",
        boxShadow: "var(--shadow-sm)"
      },
      children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx(
            "h1",
            {
              className: "text-xl sm:text-2xl font-bold tracking-tight",
              style: { color: "var(--text-primary)" },
              children: title
            }
          ),
          subtitle && /* @__PURE__ */ jsx("p", { className: "text-sm mt-1", style: { color: "var(--text-secondary)" }, children: subtitle })
        ] }),
        actions && /* @__PURE__ */ jsx("div", { className: "flex items-center gap-3 shrink-0", children: actions })
      ]
    }
  );
};
var ProductAppScaffold = ({
  children,
  header,
  mainClassName = "flex-1 min-h-0 overflow-hidden min-w-0"
}) => /* @__PURE__ */ jsxs("div", { className: "h-full min-h-0 flex flex-col", style: { background: "var(--bg-root)" }, children: [
  header,
  /* @__PURE__ */ jsx("div", { className: cn(mainClassName), children })
] });
var Toast = ({
  message,
  type = "info",
  duration = 3e3,
  persistent = false,
  onClose,
  children
}) => {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!persistent && duration > 0) {
      const timer = setTimeout(() => {
        setVisible(false);
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, persistent, onClose]);
  const typeColors = {
    success: "var(--success)",
    error: "var(--danger)",
    warning: "var(--warning)",
    info: "var(--accent)"
  };
  if (!visible) return null;
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg",
        "border backdrop-blur-sm animate-fade-in z-50 max-w-md"
      ),
      style: {
        background: "var(--bg-surface)",
        borderColor: typeColors[type],
        borderLeftWidth: "4px"
      },
      children: /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0",
            style: { background: typeColors[type] }
          }
        ),
        children ? /* @__PURE__ */ jsx("div", { className: "flex-1 min-w-0", children }) : /* @__PURE__ */ jsx("p", { className: "text-sm", style: { color: "var(--text-primary)" }, children: message }),
        /* @__PURE__ */ jsx(
          "button",
          {
            onClick: () => {
              setVisible(false);
              onClose?.();
            },
            className: "ml-auto text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors",
            children: "\xD7"
          }
        )
      ] })
    }
  );
};
var ToastContainer = ({ children }) => {
  return /* @__PURE__ */ jsx("div", { className: "fixed bottom-6 right-6 z-50 space-y-3", children });
};
var Spinner = ({ size = "md", className }) => {
  const sizeStyles3 = {
    sm: "w-4 h-4 border-2",
    md: "w-8 h-8 border-2",
    lg: "w-12 h-12 border-[3px]"
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "rounded-full animate-spin",
        sizeStyles3[size],
        className
      ),
      style: {
        border: "2px solid var(--border)",
        borderTopColor: "var(--accent)"
      }
    }
  );
};
var variantStyles = {
  success: "bg-[var(--success-dim)] text-[var(--success)]",
  error: "bg-[var(--danger-dim)] text-[var(--danger)]",
  warning: "bg-[var(--warning-dim)] text-[var(--warning)]",
  info: "bg-[var(--info-dim)] text-[var(--info)]",
  default: "bg-[var(--bg-surface-2)] text-[var(--text-secondary)]"
};
var sizeStyles = {
  sm: "px-2 py-0.5 text-[10px]",
  md: "px-2.5 py-1 text-xs"
};
var StatusBadge = React.forwardRef(
  ({ variant = "default", size = "md", className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "span",
      {
        ref,
        className: cn(
          "inline-flex items-center rounded-full font-medium",
          variantStyles[variant],
          sizeStyles[size],
          className
        ),
        ...props,
        children
      }
    );
  }
);
StatusBadge.displayName = "StatusBadge";
var TabNav = React.forwardRef(
  ({ items, activeKey, onSelect, className }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn("flex gap-0 border-b border-[var(--border-subtle)]", className),
        role: "tablist",
        children: items.map((item) => {
          const isActive = item.key === activeKey;
          return /* @__PURE__ */ jsx(
            "button",
            {
              role: "tab",
              "aria-selected": isActive,
              disabled: item.disabled,
              onClick: () => onSelect(item.key),
              className: cn(
                "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
                isActive ? "border-[var(--accent)] text-[var(--accent)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                item.disabled && "opacity-50 cursor-not-allowed"
              ),
              children: item.label
            },
            item.key
          );
        })
      }
    );
  }
);
TabNav.displayName = "TabNav";
var sizeStyles2 = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl"
};
var CloseIcon = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: "20",
    height: "20",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    children: [
      /* @__PURE__ */ jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
      /* @__PURE__ */ jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })
    ]
  }
);
var Modal = ({
  open,
  onClose,
  title,
  size = "md",
  children,
  footer,
  className
}) => {
  const handleEscape = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );
  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [open, handleEscape]);
  if (!open) return null;
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "fixed inset-0 z-50 flex items-center justify-center p-4",
        style: { background: "rgba(0, 0, 0, 0.5)" },
        onClick: onClose,
        role: "presentation",
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            role: "dialog",
            "aria-modal": "true",
            "aria-label": title,
            className: cn(
              "w-full rounded-xl overflow-hidden animate-fade-in",
              "bg-[var(--bg-surface)] border border-[var(--border-hover)]",
              "shadow-[var(--shadow-card)]",
              sizeStyles2[size],
              className
            ),
            onClick: (e) => e.stopPropagation(),
            children: [
              title && /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "flex items-center justify-between px-5 py-4 border-b border-[var(--border)]",
                  children: [
                    /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold text-[var(--text-primary)]", children: title }),
                    /* @__PURE__ */ jsx(
                      "button",
                      {
                        onClick: onClose,
                        className: "p-1 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors",
                        children: /* @__PURE__ */ jsx(CloseIcon, {})
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsx("div", { className: "overflow-y-auto max-h-[70vh] px-5 py-4", children }),
              footer && /* @__PURE__ */ jsx("div", { className: "flex items-center justify-end gap-3 px-5 py-4 border-t border-[var(--border)]", children: footer })
            ]
          }
        )
      }
    ),
    document.body
  );
};
Modal.displayName = "Modal";
var shimmerClass = "bg-gradient-to-r from-[var(--bg-surface-2)] via-[var(--bg-surface)] to-[var(--bg-surface-2)] bg-[length:200%_100%] animate-[weco-shimmer_1.5s_ease-in-out_infinite]";
var roundedStyles = {
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full"
};
var SkeletonBlock = ({
  width,
  height = "14px",
  rounded = "sm",
  className
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(shimmerClass, roundedStyles[rounded], className),
    style: { width, height },
    "aria-hidden": "true"
  }
);
SkeletonBlock.displayName = "SkeletonBlock";
var SkeletonTable = ({
  rows = 5,
  columns = 4,
  className
}) => /* @__PURE__ */ jsxs(
  "div",
  {
    className: cn("p-4 space-y-3", className),
    "aria-busy": "true",
    "aria-label": "Loading",
    children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "flex gap-4 pb-2",
          style: { borderBottom: "1px solid var(--border-subtle)" },
          children: Array.from({ length: columns }).map((_, i) => /* @__PURE__ */ jsx(
            SkeletonBlock,
            {
              width: `${Math.max(15, 30 - i * 5)}%`,
              height: "14px"
            },
            `h-${i}`
          ))
        }
      ),
      Array.from({ length: rows }).map((_, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4 py-2", children: [
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "32px", height: "32px", rounded: "full", className: "flex-shrink-0" }),
        Array.from({ length: columns - 1 }).map((_2, j) => /* @__PURE__ */ jsx(
          SkeletonBlock,
          {
            width: `${Math.max(15, 25 - j * 3)}%`,
            height: "12px"
          },
          `r-${i}-c-${j}`
        ))
      ] }, `r-${i}`))
    ]
  }
);
SkeletonTable.displayName = "SkeletonTable";
var SkeletonCard = ({ className }) => /* @__PURE__ */ jsxs(
  "div",
  {
    className: cn(
      "p-4 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)]",
      className
    ),
    "aria-busy": "true",
    "aria-label": "Loading",
    children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 mb-4", children: [
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "48px", height: "48px", rounded: "lg", className: "flex-shrink-0" }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-2 pt-1", children: [
          /* @__PURE__ */ jsx(SkeletonBlock, { width: "60%", height: "14px" }),
          /* @__PURE__ */ jsx(SkeletonBlock, { width: "40%", height: "10px" })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "100%", height: "10px" }),
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "80%", height: "10px" })
      ] })
    ]
  }
);
SkeletonCard.displayName = "SkeletonCard";
var SkeletonDetail = ({ className }) => /* @__PURE__ */ jsxs(
  "div",
  {
    className: cn("p-6 space-y-6", className),
    "aria-busy": "true",
    "aria-label": "Loading",
    children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "64px", height: "64px", rounded: "full", className: "flex-shrink-0" }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-2 flex-1", children: [
          /* @__PURE__ */ jsx(SkeletonBlock, { width: "40%", height: "20px" }),
          /* @__PURE__ */ jsx(SkeletonBlock, { width: "25%", height: "12px" })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex gap-4 pb-2", style: { borderBottom: "1px solid var(--border-subtle)" }, children: [
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "80px", height: "14px" }),
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "80px", height: "14px" }),
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "80px", height: "14px" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "100%", height: "12px" }),
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "90%", height: "12px" }),
        /* @__PURE__ */ jsx(SkeletonBlock, { width: "70%", height: "12px" })
      ] })
    ]
  }
);
SkeletonDetail.displayName = "SkeletonDetail";
var AlertCircleIcon = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: "40",
    height: "40",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    children: [
      /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "8", x2: "12", y2: "12" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "16", x2: "12.01", y2: "16" })
    ]
  }
);
function formatError(error) {
  if (!error) return "An unexpected error occurred.";
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "An unexpected error occurred.";
}
var ErrorState = React.forwardRef(
  ({ error, message, onRetry, className }, ref) => {
    const displayMessage = message ?? formatError(error);
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "flex flex-col items-center justify-center py-12 gap-3",
          className
        ),
        children: [
          /* @__PURE__ */ jsx("span", { className: "text-[var(--danger)]", children: /* @__PURE__ */ jsx(AlertCircleIcon, {}) }),
          /* @__PURE__ */ jsx(
            "p",
            {
              className: "text-sm text-center max-w-md",
              style: { color: "var(--text-primary)" },
              children: displayMessage
            }
          ),
          onRetry && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: onRetry, children: "Retry" })
        ]
      }
    );
  }
);
ErrorState.displayName = "ErrorState";
function isSeparator(entry) {
  return "type" in entry && entry.type === "separator";
}
var DropdownMenu = ({
  trigger,
  items,
  align = "right",
  header,
  className
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close]);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, close]);
  return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: cn("relative", className), children: [
    /* @__PURE__ */ jsx("div", { onClick: () => setOpen((prev) => !prev), children: trigger }),
    open && /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "absolute mt-2 rounded-xl py-1 min-w-[200px] animate-fade-in",
          "bg-[var(--bg-surface-3)] border border-[var(--border-hover)]",
          "shadow-[var(--shadow-card)] z-50",
          align === "right" ? "right-0" : "left-0"
        ),
        children: [
          header && /* @__PURE__ */ jsx("div", { className: "px-4 py-3 border-b border-[var(--border)]", children: header }),
          items.map((entry) => {
            if (isSeparator(entry)) {
              return /* @__PURE__ */ jsx(
                "div",
                {
                  className: "my-1 border-t border-[var(--border)]"
                },
                entry.key
              );
            }
            return /* @__PURE__ */ jsxs(
              "button",
              {
                disabled: entry.disabled,
                onClick: () => {
                  entry.onClick?.();
                  close();
                },
                className: cn(
                  "w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors",
                  entry.danger ? "text-[var(--danger)] hover:bg-[var(--danger-dim)]" : "text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]",
                  entry.disabled && "opacity-50 cursor-not-allowed"
                ),
                children: [
                  entry.icon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0 w-4 h-4", children: entry.icon }),
                  entry.label
                ]
              },
              entry.key
            );
          })
        ]
      }
    )
  ] });
};
DropdownMenu.displayName = "DropdownMenu";
var DefaultSearchIcon = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: "18",
    height: "18",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    children: [
      /* @__PURE__ */ jsx("circle", { cx: "11", cy: "11", r: "8" }),
      /* @__PURE__ */ jsx("line", { x1: "21", y1: "21", x2: "16.65", y2: "16.65" })
    ]
  }
);
var SearchInput = React.forwardRef(
  ({ icon, className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex items-center gap-2 rounded-lg border px-3 py-2",
          "bg-[var(--bg-surface)] border-[var(--border)]",
          "focus-within:border-[var(--accent)] focus-within:ring-2 focus-within:ring-[var(--accent-dim)]",
          "transition-all duration-150",
          className
        ),
        children: [
          /* @__PURE__ */ jsx("span", { className: "flex-shrink-0 text-[var(--text-muted)]", children: icon ?? /* @__PURE__ */ jsx(DefaultSearchIcon, {}) }),
          /* @__PURE__ */ jsx(
            "input",
            {
              ref,
              type: "search",
              className: "flex-1 min-w-0 bg-transparent text-sm outline-none text-[var(--text-primary)] placeholder:text-[var(--text-muted)]",
              ...props
            }
          )
        ]
      }
    );
  }
);
SearchInput.displayName = "SearchInput";
function DataTableInner({
  columns,
  data,
  rowKey,
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
  onRowClick,
  emptyState,
  className
}, ref) {
  const allKeys = data.map(rowKey);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.includes(k));
  const toggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : allKeys);
  }, [allSelected, allKeys, onSelectionChange]);
  const toggleRow = useCallback(
    (key) => {
      if (!onSelectionChange) return;
      onSelectionChange(
        selectedKeys.includes(key) ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key]
      );
    },
    [selectedKeys, onSelectionChange]
  );
  return /* @__PURE__ */ jsx("div", { ref, className: cn("overflow-x-auto", className), children: /* @__PURE__ */ jsxs("table", { className: "w-full", children: [
    /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "border-b border-[var(--border-subtle)]", children: [
      selectable && /* @__PURE__ */ jsx("th", { className: "px-4 py-3 text-left w-10", children: /* @__PURE__ */ jsx(
        "input",
        {
          type: "checkbox",
          checked: allSelected,
          onChange: toggleAll,
          className: "cursor-pointer"
        }
      ) }),
      columns.map((col) => /* @__PURE__ */ jsx(
        "th",
        {
          className: cn(
            "px-4 py-3 text-left text-sm font-semibold text-[var(--text-secondary)]",
            col.headerClassName
          ),
          style: col.width ? { width: col.width } : void 0,
          children: col.header
        },
        col.key
      ))
    ] }) }),
    /* @__PURE__ */ jsx("tbody", { children: data.length === 0 && emptyState ? /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { colSpan: columns.length + (selectable ? 1 : 0), children: emptyState }) }) : data.map((row, idx) => {
      const key = rowKey(row);
      const isSelected = selectedKeys.includes(key);
      return /* @__PURE__ */ jsxs(
        "tr",
        {
          className: cn(
            "border-b border-[var(--border-subtle)] transition-colors",
            "hover:bg-[var(--bg-hover)]",
            onRowClick && "cursor-pointer",
            isSelected && "bg-[var(--accent-dim)]"
          ),
          onClick: () => onRowClick?.(row),
          children: [
            selectable && /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx(
              "input",
              {
                type: "checkbox",
                checked: isSelected,
                onChange: (e) => {
                  e.stopPropagation();
                  toggleRow(key);
                },
                onClick: (e) => e.stopPropagation(),
                className: "cursor-pointer"
              }
            ) }),
            columns.map((col) => /* @__PURE__ */ jsx(
              "td",
              {
                className: cn("px-4 py-3", col.cellClassName),
                children: col.render(row, idx)
              },
              col.key
            ))
          ]
        },
        key
      );
    }) })
  ] }) });
}
var DataTable = React.forwardRef(DataTableInner);
DataTable.displayName = "DataTable";
var MOBILE_MAX_WIDTH_PX = 640;
var COMPACT_MAX_WIDTH_PX = 1024;
var MOBILE_QUERY = `(max-width: ${MOBILE_MAX_WIDTH_PX}px)`;
var COMPACT_QUERY = `(max-width: ${COMPACT_MAX_WIDTH_PX - 1}px)`;
function readBreakpoint() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return { isMobile: false, isDesktop: true, isCompactViewport: false };
  }
  const isMobile = window.matchMedia(MOBILE_QUERY).matches;
  const isCompactViewport = window.matchMedia(COMPACT_QUERY).matches;
  return { isMobile, isDesktop: !isMobile, isCompactViewport };
}
function useBreakpoint() {
  const [bp, setBp] = useState(readBreakpoint);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql1 = window.matchMedia(MOBILE_QUERY);
    const mql2 = window.matchMedia(COMPACT_QUERY);
    const sync = () => setBp(readBreakpoint());
    sync();
    const add = (mql) => {
      if (typeof mql.addEventListener === "function") mql.addEventListener("change", sync);
      else mql.addListener(sync);
    };
    const remove = (mql) => {
      if (typeof mql.removeEventListener === "function") mql.removeEventListener("change", sync);
      else mql.removeListener(sync);
    };
    add(mql1);
    add(mql2);
    return () => {
      remove(mql1);
      remove(mql2);
    };
  }, []);
  return bp;
}
var MOBILE_BREAKPOINT_PX = MOBILE_MAX_WIDTH_PX;
var COMPACT_BREAKPOINT_PX = COMPACT_MAX_WIDTH_PX;

export { Button, COMPACT_BREAKPOINT_PX, Card, DataTable, DropdownMenu, EmptyState, ErrorState, Input, MOBILE_BREAKPOINT_PX, Modal, PageHeader, PasswordInput, ProductAppScaffold, SearchInput, SkeletonBlock, SkeletonCard, SkeletonDetail, SkeletonTable, Spinner, StatusBadge, TabNav, Toast, ToastContainer, cn, useBreakpoint };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map